import { Button, createStyles, DialogContent, DialogTitle, makeStyles, TextField, Theme } from '@material-ui/core';
import React, { useMemo, useReducer } from 'react';

import {
  GetBoardBeforeSyncQuery,
  useGetDocumentToImportQuery,
  useSyncBoardMutation,
} from '../../../../graphql/gen/types';
import { operationName } from '../../../../graphql/operationName';
import { GET_FEED_BOARDS_QUERY } from '../../../../graphql/queries/GetFeedBoards';
import { logAnalytics } from '../../shared/logAnalytics';
import { DialogActions } from '../DialogActions';
import { useDialogState } from '../useDialogState';
import { BoardGoogleDocPreview, getFaqBoardSection, getFaqSections } from './BoardGoogleDocPreview';
import { documentToImportReducer } from './documentToImportReducer';
import { useInitializeDocumentToImportOnSuccess } from './useInitializeDocumentToImportOnSuccess';

type Props = {
  board: NonNullable<NonNullable<GetBoardBeforeSyncQuery['loggedIn']>['boardById']>;
};

const getUniqueKey = ({ externalEntityId, headingId }: { externalEntityId: string; headingId: string }): string =>
  `${externalEntityId}:${headingId}`;

export const SyncBoardFromGoogleDocs = ({ board }: Props): React.ReactElement => {
  if (board.source == null) {
    throw new Error(`Invalid SyncBoardFromGoogleDocs for board without source`);
  }
  const { url, headingIds } = board.source;
  const [, setDialogState] = useDialogState();
  const [documentToImport, dispatch] = useReducer(documentToImportReducer, undefined);
  const { data, loading } = useGetDocumentToImportQuery({ variables: { url }, fetchPolicy: 'no-cache' });
  const [syncBoard, { loading: syncBoardLoading }] = useSyncBoardMutation();

  const classes = useStyles();

  const faqIds = useMemo(() => {
    const result: { [key: string]: string } = {};
    for (const question of board.questions.items) {
      if (question.source != null) {
        const { externalEntityId, headingIds } = question.source;
        const key = getUniqueKey({ externalEntityId, headingId: headingIds[0] });
        result[key] = question.id;
      }
    }

    return result;
  }, [board.questions.items]);

  const handleUpdate = async (): Promise<void> => {
    if (documentToImport != null) {
      const { selectedSections, content, externalEntityId } = documentToImport;
      const boardSection = getFaqBoardSection({ selectedSections });

      if (boardSection != null) {
        const faqSections = getFaqSections({ selectedSections });
        await syncBoard({
          variables: {
            input: {
              boardId: board.id,
              boardName: boardSection.title,
              importedQuestions: [...faqSections].map((faqSection) => {
                return {
                  faqId: faqIds[getUniqueKey({ externalEntityId, headingId: faqSection.title.headingId || '' })],
                  question: faqSection.title,
                  answer: content.slice(faqSection.startIndex + 1, faqSection.endIndex),
                };
              }),
              externalEntityId,
            },
          },
          refetchQueries: [operationName(GET_FEED_BOARDS_QUERY)],
          awaitRefetchQueries: true,
        });

        logAnalytics({ name: 'synced board', details: { plexId: board.plexId, itemId: board.id } });
        setDialogState(null, 'replace');
      }
    }
  };

  useInitializeDocumentToImportOnSuccess({ data, dispatch, headingIds });

  return (
    <div className={classes.root}>
      <DialogTitle>Sync Board from Google Docs</DialogTitle>
      <DialogContent>
        <TextField
          value={board.source.url}
          disabled
          fullWidth
          variant="outlined"
          label="Google document link"
          name="Google document link"
        />

        <BoardGoogleDocPreview dispatch={dispatch} documentToImport={documentToImport} data={data} loading={loading} />
      </DialogContent>

      <DialogActions
        cta={
          <Button variant="contained" color="primary" disabled={loading || syncBoardLoading} onClick={handleUpdate}>
            Sync
          </Button>
        }
      />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
  })
);
